import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowRight, Printer, Trash2, Edit, Search, FileDown } from 'lucide-react';
import { getServiceCalls, deleteServiceCall, getBranches, getConsolidatedInvoices } from '../api';
import { toast } from '../hooks/use-toast';
import { DateRangePresets } from '../components/DateRangePresets';
import { ConsolidatedPaidBadge } from '../components/ConsolidatedPaidBadge';
import { DateGroupedTable, CallGroupedTable, groupCallsByDate } from '../components/ReportGroupedView';

const ServiceCallsReport = () => {
  const navigate = useNavigate();
  const [serviceCalls, setServiceCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [paidRatios, setPaidRatios] = useState({});
  const [viewMode, setViewMode] = useState('date');
  
  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    startDate: '',
    endDate: '',
    branch: ''
  });

  useEffect(() => {
    loadServiceCalls();
    loadBranches();
    getConsolidatedInvoices().then((invoices) => {
      const ratios = {};
      (invoices || []).forEach((inv) => {
        const paid = (inv.payments || []).reduce((s, p) => s + (parseFloat(String(p.amount ?? '').replace(/[,₪]/g, '')) || 0), 0);
        const total = parseFloat(inv.totalAmount) || 0;
        ratios[inv.id] = total > 0 ? Math.min(1, paid / total) : (paid > 0 ? 1 : 0);
      });
      setPaidRatios(ratios);
    }).catch(() => {});
  }, []);

  const lineAmount = (row) => (parseFloat(row.transactionAmount) || 0) * (parseFloat(row.quantity) || 0);
  const linePaid = (row) => lineAmount(row) * (paidRatios[row.consolidatedInvoiceId] || 0);
  const callAmount = (call) => (call.workItems || []).reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.transactionAmount) || 0), 0);
  const callPaid = (call) => callAmount(call) * (paidRatios[call.consolidatedInvoiceId] || 0);
  const money = (n) => n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    applyFilters();
  }, [serviceCalls, searchFilters]);

  const loadServiceCalls = async () => {
    try {
      const data = await getServiceCalls();
      setServiceCalls(data);
    } catch (error) {
      console.error('Error loading service calls:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את הקריאות',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...serviceCalls];

    // Filter by date range
    if (searchFilters.startDate) {
      filtered = filtered.filter(call => {
        const callDate = new Date(call.callDate || call.date);
        const startDate = new Date(searchFilters.startDate);
        return callDate >= startDate;
      });
    }

    if (searchFilters.endDate) {
      filtered = filtered.filter(call => {
        const callDate = new Date(call.callDate || call.date);
        const endDate = new Date(searchFilters.endDate);
        endDate.setHours(23, 59, 59, 999);
        return callDate <= endDate;
      });
    }

    // Filter by branch
    if (searchFilters.branch) {
      filtered = filtered.filter(call => 
        call.branch.toLowerCase().includes(searchFilters.branch.toLowerCase())
      );
    }

    setFilteredCalls(filtered);
  };

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      startDate: '',
      endDate: '',
      branch: ''
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Flatten work items for table display - must be before print functions
  const getFlattenedData = () => {
    const flattened = [];
    filteredCalls.forEach(call => {
      if (call.workItems && call.workItems.length > 0) {
        call.workItems.forEach((item, index) => {
          const rowId = `${call.id}-${index}`;
          flattened.push({
            rowId,
            callId: call.id,
            callNumber: call.callNumber,
            consolidatedInvoiceId: call.consolidatedInvoiceId || '',
            date: call.callDate || call.date,
            branch: call.branch,
            customerName: call.customerName,
            employeeName: call.employeeName || '',
            partName: item.partName,
            quantity: item.quantity,
            transactionAmount: item.transactionAmount,
            isFirstItem: index === 0
          });
        });
      } else {
        flattened.push({
          rowId: `${call.id}-0`,
          callId: call.id,
          callNumber: call.callNumber,
          consolidatedInvoiceId: call.consolidatedInvoiceId || '',
          date: call.callDate || call.date,
          branch: call.branch,
          customerName: call.customerName,
          employeeName: call.employeeName || '',
          partName: call.issueDescription || '',
          quantity: '',
          transactionAmount: '',
          isFirstItem: true
        });
      }
    });
    return flattened;
  };

  const flattenedData = getFlattenedData();
  const tableTotals = (() => {
    const rows = flattenedData.filter(row => selectedRows.size === 0 || selectedRows.has(row.rowId));
    const total = rows.reduce((s, row) => s + lineAmount(row), 0);
    const paid = rows.reduce((s, row) => s + linePaid(row), 0);
    return { total, paid, debt: Math.max(0, total - paid) };
  })();
  const groupedTotals = (() => {
    const total = filteredCalls.reduce((s, c) => s + callAmount(c), 0);
    const paid = filteredCalls.reduce((s, c) => s + callPaid(c), 0);
    return { total, paid, debt: Math.max(0, total - paid) };
  })();

  const generatePrintContent = (detailed = true) => {
    const dataToprint = flattenedData.filter(row => 
      selectedRows.size === 0 || selectedRows.has(row.rowId)
    );
    const printCallIds = new Set(dataToprint.map(r => r.callId));
    const dateGroups = groupCallsByDate(filteredCalls.filter(c => printCallIds.has(c.id)));
    const span = detailed ? 7 : 3;
    const headHtml = detailed
      ? '<th>קריאה #</th><th>תאריך</th><th>שם סניף</th><th>איש קשר</th><th>עובד</th><th>שם חלק</th><th>כמות</th><th>סכום עסקה</th>'
      : '<th>תאריך</th><th>מס\' קריאות</th><th>סניפים</th><th>סכום כולל</th>';
    const bodyHtml = detailed
      ? dataToprint.map(row => `
              <tr>
                <td>${row.callNumber != null ? '#' + row.callNumber : ''}${row.isFirstItem && row.employeeName ? `<div style="font-size:11px;color:#555">${row.employeeName}</div>` : ''}</td>
                <td>${new Date(row.date).toLocaleDateString('he-IL')}</td>
                <td>${row.branch}</td>
                <td>${row.customerName}</td>
                <td>${row.employeeName}</td>
                <td>${row.partName}</td>
                <td>${row.quantity}</td>
                <td>₪${lineAmount(row).toFixed(2)}</td>
              </tr>`).join('')
      : dateGroups.map(([date, calls]) => `
              <tr>
                <td>${new Date(date).toLocaleDateString('he-IL')}</td>
                <td>${calls.length}</td>
                <td>${[...new Set(calls.map(c => c.branch).filter(Boolean))].join(', ')}</td>
                <td>₪${calls.reduce((s, c) => s + callAmount(c), 0).toFixed(2)}</td>
              </tr>`).join('');
    
    const total = dataToprint.reduce((sum, row) => 
      sum + ((parseFloat(row.transactionAmount) || 0) * (parseFloat(row.quantity) || 0)), 0
    );
    
    const totalPaid = dataToprint.reduce((sum, row) => sum + linePaid(row), 0);
    const totalDebt = Math.max(0, total - totalPaid);

    return `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>דווח מכללה טכנולוגית</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; direction: rtl; }
          h1 { text-align: center; color: #1e40af; margin-bottom: 5px; }
          .date { text-align: center; color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #3b82f6; color: white; padding: 12px; border: 1px solid #333; }
          td { padding: 10px; border: 1px solid #333; text-align: center; }
          tr:nth-child(even) { background-color: #f3f4f6; }
          .total-row { background-color: #dbeafe; font-weight: bold; }
          .total-row td { padding: 15px; }
          .debt-row { background-color: #fee2e2; font-weight: bold; }
          .debt-row td { padding: 15px; color: #dc2626; }
          .paid-row { background-color: #dcfce7; font-weight: bold; }
          .paid-row td { padding: 15px; color: #15803d; }
          .debt-row.zero td { color: #15803d; }
          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div style="text-align: center; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; margin-bottom: 20px;">
          <h1 style="color: #1e40af; margin: 0 0 5px 0; font-size: 24px;">אימן שירותי מחשוב</h1>
          <p style="font-size: 13px; color: #374151; margin: 0 0 8px 0;">התקנת רשתות תקשורת | מכירה והתקנת מחשבים נייחים וניידים | מצלמות ומעגל סגור | אינטרקומים</p>
          <p style="font-size: 12px; color: #6b7280; margin: 0;">📧 aiman@aimanpc.com | 📞 052-576-7555</p>
        </div>
        <h2 style="text-align: center; color: #1e40af; margin-bottom: 5px; font-size: 20px;">דווח מכללה טכנולוגית</h2>
        <p class="date">תאריך הדפסה: ${new Date().toLocaleDateString('he-IL')}</p>
        <table>
          <thead>
            <tr>${headHtml}</tr>
          </thead>
          <tbody>
            ${bodyHtml}
            <tr class="total-row">
              <td colspan="${span}">סה"כ ${selectedRows.size > 0 ? `(${selectedRows.size} נבחרים)` : 'כולל'}</td>
              <td>₪${total.toFixed(2)}</td>
            </tr>
            <tr class="paid-row">
              <td colspan="${span}">שולם בחשבון מרוכז</td>
              <td>₪${totalPaid.toFixed(2)}</td>
            </tr>
            <tr class="debt-row${totalDebt < 0.01 ? ' zero' : ''}">
              <td colspan="${span}">יתרת חוב</td>
              <td>₪${totalDebt.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </body>
      </html>
    `;
  };

  const printWithIframe = (content) => {
    // Create hidden iframe for printing
    let printFrame = document.getElementById('printFrame');
    if (!printFrame) {
      printFrame = document.createElement('iframe');
      printFrame.id = 'printFrame';
      printFrame.style.position = 'absolute';
      printFrame.style.top = '-10000px';
      printFrame.style.left = '-10000px';
      document.body.appendChild(printFrame);
    }
    
    const frameDoc = printFrame.contentWindow || printFrame.contentDocument;
    const doc = frameDoc.document || frameDoc;
    
    doc.open();
    doc.write(content);
    doc.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
    }, 500);
  };

  const handlePrint = (detailed) => {
    printWithIframe(generatePrintContent(detailed));
  };

  const handlePrintPDF = () => {
    printWithIframe(generatePrintContent(viewMode === 'items'));
    toast({
      title: 'הדפסה',
      description: 'בחר "שמור כ-PDF" בחלון ההדפסה',
    });
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      const allRowIds = flattenedData.map(row => row.rowId);
      setSelectedRows(new Set(allRowIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (rowId, checked) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRows(newSelected);
  };

  const isAllSelected = () => {
    return flattenedData.length > 0 && selectedRows.size === flattenedData.length;
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק קריאה זו?')) {
      try {
        await deleteServiceCall(id);
        toast({
          title: 'הצלחה',
          description: 'הקריאה נמחקה בהצלחה',
        });
        // Reload the service calls after deletion
        loadServiceCalls();
      } catch (error) {
        console.error('Error deleting service call:', error);
        toast({
          title: 'שגיאה',
          description: 'לא ניתן למחוק את הקריאה',
          variant: 'destructive'
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center print:hidden">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">דווח מכללה טכנולוגית</h1>
            <p className="text-gray-600">סך הכל {filteredCalls.length} קריאות ({flattenedData.length} פריטים)</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handlePrintPDF}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <FileDown className="ml-2 w-5 h-5" />
              הדפסה PDF
            </Button>
            <Button
              onClick={() => handlePrint(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              data-testid="print-detailed-btn"
            >
              <Printer className="ml-2 w-5 h-5" />
              הדפסה עם פירוט
            </Button>
            <Button
              onClick={() => handlePrint(false)}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
              data-testid="print-summary-btn"
            >
              <Printer className="ml-2 w-5 h-5" />
              הדפסה בלי פירוט
            </Button>
            <Button
              onClick={() => navigate('/reports')}
              variant="outline"
              className="border-2 hover:bg-gray-50 transition-colors"
            >
              <ArrowRight className="ml-2 w-5 h-5" />
              חזור לדווח
            </Button>
          </div>
        </div>

        {/* Search Filters */}
        <Card className="shadow-lg mb-8 print:hidden">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center">
              <Search className="ml-2 w-6 h-6" />
              חיפוש וסינון
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <DateRangePresets
              startDate={searchFilters.startDate}
              endDate={searchFilters.endDate}
              onChange={(startDate, endDate) => setSearchFilters(prev => ({ ...prev, startDate, endDate }))}
            />
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="text-base font-semibold">
                  מתאריך
                </Label>
                <Input
                  id="startDate"
                  data-testid="start-date-input"
                  type="date"
                  value={searchFilters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="text-base border-2"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate" className="text-base font-semibold">
                  עד תאריך
                </Label>
                <Input
                  id="endDate"
                  data-testid="end-date-input"
                  type="date"
                  value={searchFilters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="text-base border-2"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch" className="text-base font-semibold">
                  סניף
                </Label>
                <select
                  id="branch"
                  value={searchFilters.branch}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full p-2 text-base border-2 rounded-md"
                >
                  <option value="">כל הסניפים</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.name}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={applyFilters}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg px-8 py-6"
              >
                <Search className="ml-2 w-5 h-5" />
                חיפוש
              </Button>
              <Button
                onClick={clearFilters}
                variant="outline"
                className="border-2 text-lg px-8 py-6"
              >
                נקה סינון
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Branch Summary */}
        {filteredCalls.length > 0 && (() => {
          const byBranch = {};
          filteredCalls.forEach(call => {
            const b = call.branch || 'ללא סניף';
            byBranch[b] = byBranch[b] || { count: 0, total: 0, paid: 0 };
            byBranch[b].count += 1;
            byBranch[b].total += callAmount(call);
            byBranch[b].paid += callPaid(call);
          });
          const rows = Object.entries(byBranch).sort((a, b) => b[1].total - a[1].total);
          const grand = rows.reduce((s, [, v]) => s + v.total, 0);
          const grandPaid = rows.reduce((s, [, v]) => s + v.paid, 0);
          const debtOf = (total, paid) => Math.max(0, total - paid);
          return (
            <Card className="shadow-lg mb-8" data-testid="branch-summary">
              <CardHeader className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
                <CardTitle className="text-xl font-bold">סיכום לפי סניף{searchFilters.startDate || searchFilters.endDate ? ' בתקופה שנבחרה' : ''}</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {rows.map(([branch, v]) => (
                    <div key={branch} className="rounded-lg border-2 border-teal-100 bg-teal-50/50 p-3" data-testid={`branch-summary-${branch}`}>
                      <p className="font-bold text-gray-900 truncate">{branch}</p>
                      <p className="text-sm text-gray-600">{v.count} קריאות</p>
                      <p className="text-lg font-black text-teal-700">₪{money(v.total)}</p>
                      <p className="text-xs font-semibold text-green-700" data-testid={`branch-summary-paid-${branch}`}>שולם: ₪{money(v.paid)}</p>
                      <p className={`text-sm font-bold ${debtOf(v.total, v.paid) < 0.01 ? 'text-green-700' : 'text-red-600'}`} data-testid={`branch-summary-debt-${branch}`}>חוב: ₪{money(debtOf(v.total, v.paid))}</p>
                    </div>
                  ))}
                  <div className="rounded-lg border-2 border-teal-600 bg-teal-600 text-white p-3" data-testid="branch-summary-total">
                    <p className="font-bold">סה"כ</p>
                    <p className="text-sm opacity-90">{filteredCalls.length} קריאות</p>
                    <p className="text-lg font-black">₪{money(grand)}</p>
                    <p className="text-xs font-semibold opacity-90" data-testid="branch-summary-total-paid">שולם: ₪{money(grandPaid)}</p>
                    <p className="text-sm font-black" data-testid="branch-summary-total-debt">חוב: ₪{money(debtOf(grand, grandPaid))}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Print Header */}
        <div className="hidden print-header mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">דווח מכללה טכנולוגית</h1>
          <p className="text-gray-600">תאריך הדפסה: {new Date().toLocaleDateString('he-IL')}</p>
        </div>

        {/* View mode toggle */}
        {flattenedData.length > 0 && (
          <div className="flex gap-2 mb-4 print:hidden" data-testid="report-view-toggle">
            {[['date', 'לפי תאריך'], ['call', 'לפי קריאה'], ['items', 'פירוט פריטים']].map(([mode, label]) => (
              <Button key={mode} size="sm" variant={viewMode === mode ? 'default' : 'outline'} onClick={() => setViewMode(mode)} data-testid={`report-view-${mode}`}>
                {label}
              </Button>
            ))}
            {viewMode !== 'items' && <span className="text-sm text-gray-500 self-center mr-2">העבר את העכבר מעל סכום כדי לראות פירוט</span>}
          </div>
        )}

        {flattenedData.length > 0 && viewMode !== 'items' ? (
          <Card className="shadow-2xl border-0">
            <CardContent className="p-6">
              <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
                {viewMode === 'date'
                  ? <DateGroupedTable calls={filteredCalls} money={money} callAmount={callAmount} totals={groupedTotals} paidRatios={paidRatios} />
                  : <CallGroupedTable calls={filteredCalls} money={money} callAmount={callAmount} totals={groupedTotals} paidRatios={paidRatios} />}
              </div>
            </CardContent>
          </Card>
        ) : flattenedData.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <p className="text-xl text-gray-500">
                {serviceCalls.length === 0 ? 'אין קריאות עדיין' : 'לא נמצאו תוצאות מתאימות לסינון'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-2xl border-0 print:shadow-none print:border print:border-gray-300">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white print:hidden">
              <CardTitle className="text-2xl font-bold flex justify-between items-center">
                <span>תוצאות חיפוש - {flattenedData.length} פריטים</span>
                {selectedRows.size > 0 && (
                  <span className="text-lg font-normal bg-white/20 px-3 py-1 rounded">
                    נבחרו {selectedRows.size} פריטים להדפסה
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 print:p-0">
              <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
                <table className="w-full border-collapse bg-white">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white print:bg-gray-200 print:text-black">
                      <th className="border border-gray-300 p-3 text-center print:hidden w-12">
                        <Checkbox
                          checked={isAllSelected()}
                          onCheckedChange={handleSelectAll}
                          className="border-white data-[state=checked]:bg-white data-[state=checked]:text-blue-600"
                        />
                      </th>
                      <th className="border border-gray-300 p-3 text-center">קריאה #</th>
                      <th className="border border-gray-300 p-3 text-center">תאריך</th>
                      <th className="border border-gray-300 p-3 text-center">שם סניף</th>
                      <th className="border border-gray-300 p-3 text-center">איש קשר</th>
                      <th className="border border-gray-300 p-3 text-center">עובד</th>
                      <th className="border border-gray-300 p-3 text-center">שם חלק</th>
                      <th className="border border-gray-300 p-3 text-center">כמות</th>
                      <th className="border border-gray-300 p-3 text-center">סכום עסקה</th>
                      <th className="border border-gray-300 p-3 text-center print:hidden">פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flattenedData.map((row, idx) => {
                      const isSelected = selectedRows.has(row.rowId);
                      const showInPrint = selectedRows.size === 0 || isSelected;
                      return (
                        <tr 
                          key={idx} 
                          className={`hover:bg-blue-50 print:hover:bg-white ${isSelected ? 'bg-blue-50' : ''} ${showInPrint ? '' : 'print:hidden'}`}
                          data-print-show={showInPrint ? 'true' : 'false'}
                        >
                          <td className="border border-gray-300 p-3 text-center print:hidden">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleSelectRow(row.rowId, checked)}
                            />
                          </td>
                          <td className="border border-gray-300 p-3 text-center font-bold text-indigo-700">
                            {row.callNumber != null ? `#${row.callNumber}` : '-'}
                            {row.isFirstItem && row.employeeName && <div className="text-xs font-normal text-gray-600" data-testid={`report-callnum-employee-${row.rowId}`}>{row.employeeName}</div>}
                          </td>
                          <td className="border border-gray-300 p-3 text-center">
                            {new Date(row.date).toLocaleDateString('he-IL')}
                          </td>
                          <td className="border border-gray-300 p-3 text-center">
                            {row.branch}
                          </td>
                          <td className="border border-gray-300 p-3 text-center">
                            {row.customerName}
                            {row.isFirstItem !== false && <div><ConsolidatedPaidBadge compact invoiceId={row.consolidatedInvoiceId} ratio={paidRatios[row.consolidatedInvoiceId]} testId={`report-consolidated-paid-${row.rowId}`} /></div>}
                          </td>
                          <td className="border border-gray-300 p-3 text-center" data-testid={`report-employee-${row.rowId}`}>
                            {row.employeeName}
                          </td>
                          <td className="border border-gray-300 p-3 text-right">
                            {row.partName}
                          </td>
                          <td className="border border-gray-300 p-3 text-center">
                            {row.quantity}
                          </td>
                          <td className="border border-gray-300 p-3 text-center font-semibold text-blue-700 print:text-black">
                            ₪{((parseFloat(row.transactionAmount) || 0) * (parseFloat(row.quantity) || 0)).toFixed(2)}
                          </td>
                          <td className="border border-gray-300 p-3 text-center print:hidden">
                            {row.isFirstItem && (
                              <div className="flex gap-2 justify-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/new-service-call?id=${row.callId}`)}
                                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDelete(row.callId)}
                                  className="border-red-500 text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Total Row */}
                    <tr className="bg-blue-100 font-bold total-row" data-print-show="true">
                      <td className="border border-gray-300 p-3 print:hidden"></td>
                      <td colSpan="7" className="border border-gray-300 p-3 text-center">
                        {selectedRows.size > 0 ? `סה"כ נבחרים (${selectedRows.size})` : 'סה"כ כולל'}
                      </td>
                      <td className="border border-gray-300 p-3 text-center text-blue-900 print:text-black" data-testid="report-total-amount">
                        ₪{tableTotals.total.toFixed(2)}
                      </td>
                      <td className="border border-gray-300 p-3 print:hidden"></td>
                    </tr>
                    <tr className="bg-green-50 font-bold" data-print-show="true">
                      <td className="border border-gray-300 p-3 print:hidden"></td>
                      <td colSpan="7" className="border border-gray-300 p-3 text-center text-green-800">שולם בחשבון מרוכז</td>
                      <td className="border border-gray-300 p-3 text-center text-green-700" data-testid="report-total-paid">₪{tableTotals.paid.toFixed(2)}</td>
                      <td className="border border-gray-300 p-3 print:hidden"></td>
                    </tr>
                    <tr className={`${tableTotals.debt < 0.01 ? 'bg-green-100' : 'bg-red-50'} font-bold`} data-print-show="true">
                      <td className="border border-gray-300 p-3 print:hidden"></td>
                      <td colSpan="7" className="border border-gray-300 p-3 text-center">יתרת חוב</td>
                      <td className={`border border-gray-300 p-3 text-center ${tableTotals.debt < 0.01 ? 'text-green-700' : 'text-red-600'}`} data-testid="report-total-debt">₪{tableTotals.debt.toFixed(2)}</td>
                      <td className="border border-gray-300 p-3 print:hidden"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            background: white !important;
          }
          
          /* Hide non-printable elements */
          .print\\:hidden, .print-hide {
            display: none !important;
          }
          
          /* Show print header */
          .print-header {
            display: block !important;
          }
          
          /* Table styles for print */
          table {
            width: 100% !important;
            border-collapse: collapse !important;
          }
          
          th, td {
            border: 1px solid #333 !important;
            padding: 8px !important;
          }
          
          thead tr {
            background-color: #e5e7eb !important;
            color: black !important;
          }
          
          /* Hide rows that are not selected */
          tr[data-print-show="false"] {
            display: none !important;
          }
          
          /* Total row */
          .print\\:bg-gray-100, tfoot tr, .total-row {
            background-color: #f3f4f6 !important;
          }
          
          .print\\:text-black {
            color: #000 !important;
          }
          
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          
          .print\\:border {
            border: 1px solid #d1d5db !important;
          }
          
          .print\\:p-0 {
            padding: 0 !important;
          }
          
          @page {
            margin: 1cm;
            size: landscape;
          }
        }
      `}</style>
    </div>
  );
};

export default ServiceCallsReport;
